import React, { useEffect, useState } from 'react';
import {
  Col,
  Row,
  Container,
  Spinner,
} from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { clearError, selectFinaleState } from '../features/finale/finaleSlice';
import { releaseWantedBooking, selectBookingState } from '../features/bookings/bookingsSlice';
import {
  pineAppleDay, pineAppleTime,
} from '../lib/utils';
import TimeOutErroModal from './TimeoutErrorModal';
import { releaseReservation, selectReservation } from '../features/reservations/reservationsSlice';
import { selectAddress } from '../features/address/addressSlice';
import { selectSignUp } from '../features/signup/signupSlice';
import { bookingWindow } from '../lib/bookingWindow';
import { selectDiscount } from '../features/discount/discountSlice';
import { trackPurchase } from '../lib/analytics';
import { isCommunityFibrePlan } from '../features/finale/orderAPI';

export interface FinProps {
  previousStep: any
}

interface CommunityFibreNextStepsProps {
  // The day the customer asked for, as YYYY-MM-DD.
  preferredDate?: string;
}

// The text for a Community Fibre order after it is placed.
const CommunityFibreNextSteps = (
  { preferredDate }: CommunityFibreNextStepsProps,
) => (
  <>
    <p className="text-spaced">
      Thanks for signing up with Pineapple Net. We&apos;re getting everything
      ready to get your internet connected.
    </p>
    <h5 className="mt-4">What happens next?</h5>
    <p className="text-spaced">
      <strong>Your installation</strong>
      <br />
      Within the next two business days, you’ll receive a call from Community
      Fibre’s installation partner to confirm your preferred installation date.
      {preferredDate ? (
        <>
          {' '}
          Your current preferred installation date is
          {' '}
          <strong>{pineAppleDay(new Date(`${preferredDate}T00:00:00`))}</strong>
          .
        </>
      ) : null}
    </p>
    <p className="text-spaced">
      On the day, an installer will visit your apartment to complete the setup
      and get your Pineapple Net service up and running. Please ensure
      you&apos;re home for the appointment.
    </p>
    <p className="text-spaced">
      <strong>Your first payment</strong>
      <br />
      Your payment method has been validated and is ready for your service.
      Your first payment will be taken 15 days after your internet service is
      activated.
    </p>
    <p className="text-spaced">
      <strong>Need a hand?</strong>
      <br />
      If you have any questions about your plan, installation or what happens
      next, our Pineapple Net Customer Service team is here to help.
    </p>
    <p className="text-spaced">
      Thanks again for choosing Pineapple Net. We look forward to bringing you
      {' '}
      <strong>refreshingly fast internet!</strong>
    </p>
  </>
);

const Fin = ({ previousStep }: FinProps) => {
  const dispatch = useAppDispatch();
  const finaleState = useAppSelector(selectFinaleState);
  const bookingsState = useAppSelector(selectBookingState);
  const [showTimeOutErr, setShowTimeOutError] = useState(false);

  const addr = useAppSelector(selectAddress);
  const signUp = useAppSelector(selectSignUp);
  // The installer slot, where one was booked. A Community Fibre order has
  // no slot, because Community Fibre book the visit with the customer.
  const slot = bookingWindow(bookingsState.wantedBookingId, addr.state);
  const communityFibre = isCommunityFibrePlan(signUp.plan);
  const discount = useAppSelector(selectDiscount);
  const reservation = useAppSelector(selectReservation);

  // Only once the back end has confirmed the order: the payment step
  // submitting is not a purchase, the card can still be declined here.
  useEffect(() => {
    if (finaleState.status !== 'idle' || !finaleState.value.success) return;
    // The install response carries no order id; the reservation id is
    // unique per order and is what the back end received.
    const transactionId = reservation?.reservation_id
      || `${bookingsState.wantedBookingId.event_id}`;
    trackPurchase(transactionId, signUp, addr, discount);
  }, [finaleState.status, finaleState.value.success]);

  const goBack = () => {
    dispatch(releaseWantedBooking());
    dispatch(releaseReservation());
    dispatch(clearError());
    previousStep();
  };

  useEffect(() => {
    if (finaleState?.value?.err && finaleState?.value?.err?.includes("booking is expired")) {
      setShowTimeOutError(true);
    }
  }, [finaleState.value.err]);

  if (finaleState.status === 'loading') {
    return (
      <div className="center">
        <Spinner
          className="center"
          animation="grow"
          variant="primary"
          style={{ width: '4rem', height: '4rem' }}
        />
      </div>
    );
  }

  return (
    <Container>
      {showTimeOutErr && <TimeOutErroModal altText="Reservation not available." onSubmit={() => goBack()} />}
      <Row className="justify-content-center">
        <Col>
          {finaleState.value.success ? (
            <h1 className="fw-bold text-primary text-center mb-4">
              {communityFibre
                ? 'Welcome to Pineapple Net!'
                : 'Welcome onboard!'}
            </h1>
          ) : (
            <h1 className="fw-bold text-primary text-center mb-4">
              We&apos;re sorry!
            </h1>
          )}
        </Col>
      </Row>
      <Row className="justify-content-center text-center">
        <Col>
          <div
            style={{ borderTop: '2px solid' }}
            className="position-sticky
          mt-2
          align-self-end
          top-0
          py-2
          border-primary"
          >
            {finaleState.value.success ? (
              <h4>
                {communityFibre ? (
                  <>
                    You&apos;re one step closer to a refreshingly better
                    internet experience.
                  </>
                ) : (
                  <>
                    We&apos;re getting ready to connect your super fast
                    internet.
                  </>
                )}
              </h4>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
        </Col>
      </Row>
      <Row className="justify-content-center text-center">
        <Col xs={8}>
          {finaleState.value.success && communityFibre ? (
            <CommunityFibreNextSteps preferredDate={signUp.preferredDate} />
          ) : null}
          {finaleState.value.success && !communityFibre ? (
            <>
              <p className="text-spaced">
                Your payment method has been validated in readiness for your
                connection. It&apos;s important to note that your 1st payment will
                occur 15 days after your service has been activated.
              </p>
              {slot ? (
                <p className="text-spaced">
                  In the meantime, you&apos;ll receive an SMS from DGtek
                  confirming your installation date and time of
                  {' '}
                  <strong>
                    {slot.day}
                    {' '}
                    between
                    {' '}
                    {slot.from}
                    {' '}
                    and
                    {' '}
                    {slot.to}
                  </strong>
                  .
                  On the day of your installation, DGtek installers will be
                  on-site during your scheduled window to configure your
                  internet connection, and connect you to their fibre optic
                  network.
                </p>
              ) : null}
              <p className="text-spaced">
                If you have any further questions, please reach out to our
                contact centre – they&apos;d be happy to assist.
              </p>
              <p className="text-spaced">
                Thanks again for choosing to connect your internet with
                Pineapple Net. We look forward to bringing you Refreshingly
                fast Internet.
              </p>
            </>
          ) : null}
          {!finaleState.value.success ? (
            <>
              <p className="text-spaced">{finaleState.value.err}</p>
              <hr />
              <p className="text-spaced">
                Unfortunately an error has occured with your order. We want to
                get you connected with Pineapple Net as quickly as possible.
                Please call us on 1300 857 501 or email us at
                sales@pineapple.net.au so that we can finalise your connection.
              </p>
            </>
          ) : null}
        </Col>
      </Row>
    </Container>
  );
};

export default Fin;
